// @flow
import * as React from 'react';
import { Box, Text } from 'grommet';
import ReactTooltip from 'react-tooltip';
import { AVATAR_BORDER_RADIUS } from 'esm-web-pro/styles/theme';
import { getInitialsFromUsername } from 'esm-web-pro/components/maps/MapUser';
import { APIContext } from 'speed-js-react';
import Spinner from 'esm-web-pro/components/forms/Spinner';


export const userBadgeBorderStyle = {
    color: 'border',
    size: '1px',
    side: 'all',
};

type Props = {
    username: string,
    userFullName: string,
    margin?: Object | string,
    pad?: Object | string,
    size: string,
    tooltipPlace: string,
    tooltipKey?: ?string,
    tooltipText?: string,
    borderColor?: string,
};

const UserBadge = (props: Props) => {
    const {
        username, userFullName, margin, size, pad, tooltipPlace, tooltipKey, tooltipText, borderColor,
    } = props;

    const { apiClient } = React.useContext(APIContext);

    const [user, setUser] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        // Get user by username in order to see if avatar is available
        apiClient.resources.user.list({ username_exact: username })
            .then((response) => {
                if (response.results.length > 0) {
                    setUser(response.results[0]);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [username, apiClient.resources.user]);

    if (loading) {
        return (
            <Spinner
                type="clipLoader"
                loading
                blocking={false}
                color="black"
                coverStyle={{
                    position: 'relative',
                    width: 'auto',
                    height: 'auto',
                }}
            />
        );
    }

    if (user && user.avatar) {
        return (
            <Box
                align="center"
                justify="center"
                overflow="hidden"
                round={{ size: AVATAR_BORDER_RADIUS }}
                width="32px"
                height="32px"
                margin={{ top: '4px' }}
            >
                <img
                    data-tip
                    data-for={`${username}-user-avatar`}
                    src={user.avatar}
                    alt="event-user"
                    className="vertical-timeline-element--audit-event--avatar"
                />
                <ReactTooltip
                    id={`${username}-user-avatar`}
                    effect="solid"
                    place={tooltipPlace}
                    className="react-tooltip"
                >
                    <Text size="small">
                        {username}
                    </Text>
                </ReactTooltip>
            </Box>
        );
    }

    return (
        <>
            <Box
                data-tip
                data-for={tooltipKey || username}
                width={size}
                height={size}
                align="center"
                justify="center"
                margin={margin}
                pad={pad}
                title={username}
                border={borderColor ? { ...userBadgeBorderStyle, color: borderColor } : userBadgeBorderStyle}
                round={{ size: AVATAR_BORDER_RADIUS }}
            >
                <Text size="medium">
                    {getInitialsFromUsername(userFullName || username)}
                </Text>
            </Box>
            <ReactTooltip
                id={tooltipKey || username}
                effect="solid"
                place={tooltipPlace}
                className="react-tooltip"
            >
                <Text size="small">
                    {tooltipText || username}
                </Text>
            </ReactTooltip>
        </>
    );
};

UserBadge.defaultProps = {
    margin: { left: 'xsmall' },
    size: '32px',
    pad: 'xsmall',
    tooltipPlace: 'top',
    tooltipKey: null,
    tooltipText: undefined,
    borderColor: undefined,
};

export default UserBadge;
